/**
 * Per-member cache of signed photo URLs, shared by every member-photo surface.
 *
 * WHY THIS EXISTS
 * ---------------
 * `useBulkMemberPhotos` is keyed on the *id array* it was handed, so React
 * Query treats every distinct list as a separate cache entry. Changing page,
 * sorting a column, typing in the header search or opening a report all produce
 * a brand-new cold key, and each one re-fetched photos the browser already had
 * — two round trips (the RPC, then a Storage batch-sign) for members whose
 * thumbnails were already on screen a second ago.
 *
 * In production that made the two photo endpoints 45% of ALL API traffic
 * (162,060 of 361,191 requests in 24h; 61% at the 11:00 Bali peak) against a
 * roster of ~2,100 members with ~1,957 photos between them. Nearly all of it
 * was re-fetching the same handful of rows.
 *
 * This module holds the answer *per member*, so the array-keyed query above it
 * becomes a cheap projection: a list whose members are all cached resolves with
 * zero network requests, and a partially-cached list only asks for the ids it
 * is actually missing.
 *
 * WHY A PLAIN MAP RATHER THAN THE REACT QUERY CACHE
 * -------------------------------------------------
 * Per-member entries are read on every render of every list — up to ~2,000
 * lookups per render. `queryClient.getQueryData` hashes the key (a
 * `JSON.stringify` per call) on each lookup, which is the wrong cost profile
 * for a hot read path. A `Map` keyed on a pre-built string is O(1). The trade
 * is that this cache is invisible to the React Query devtools and is NOT
 * cleared by `queryClient.clear()`, so sign-out and PIN-switch must call
 * `clearMemberPhotoCache()` explicitly — see `useAuth` and `usePinSwitch`.
 *
 * TWO EXPIRIES, BOTH ENFORCED
 * ---------------------------
 * 1. `MEMBER_PHOTO_CACHE_TTL_MS` bounds how stale the *data* may be: a photo
 *    taken in another browser (or by a member in the mobile app) shows up here
 *    within 30 minutes. A photo taken in THIS browser appears immediately,
 *    because the create/delete mutations call `invalidateMemberPhoto`.
 * 2. `SIGNED_URL_EXPIRY_HEADROOM_MS` bounds how stale the *URL* may be. Signed
 *    URLs live one hour; an entry stops being usable a minute before its
 *    earliest signed URL dies, which matches the refresh buffer in
 *    `getSignedUrlRefreshDelay` so the timer and this cache agree on when a URL
 *    has gone bad. Serving a URL that expires mid-render is a broken image.
 */

/** How long a cached answer may be reused before we re-ask the database. */
export const MEMBER_PHOTO_CACHE_TTL_MS = 30 * 60 * 1000;

/**
 * Treat a signed URL as already gone this long before it truly expires — the
 * same 60s buffer `getSignedUrlRefreshDelay` refreshes on, so the refresh timer
 * and the cache never disagree about whether an entry is still good.
 */
export const SIGNED_URL_EXPIRY_HEADROOM_MS = 60 * 1000;

/**
 * Upper bound on retained entries. The roster is ~2,100 members across two
 * buckets, so this is roughly 2x headroom and exists only to stop an unbounded
 * session (reception leaves a tab open for days) from growing without limit.
 */
export const MEMBER_PHOTO_CACHE_MAX_ENTRIES = 10_000;

/**
 * The expiry fields `addSignedUrlsToPhotos` stamps onto every signed photo.
 * Any photo shape carrying these can live in this cache — webcam photos and
 * member-uploaded profile photos have different row shapes but both qualify.
 */
export interface CacheablePhoto {
  original_photo_url_expires_at?: number | null;
  thumbnail_photo_url_expires_at?: number | null;
  large_photo_url_expires_at?: number | null;
}

interface CacheRecord<T> {
  /** `null` means "this member has no photo" — a real answer, not a miss. */
  photo: T | null;
  cachedAt: number;
}

/** Keyed `bucket::memberId` so the two photo buckets can never collide. */
const cache = new Map<string, CacheRecord<CacheablePhoto>>();

/**
 * Bumped by every invalidation and every clear.
 *
 * Fetches are asynchronous, so a response can arrive AFTER the thing it
 * describes was invalidated — and writing it then would undo the invalidation:
 *
 *  - a bulk fetch starts; the admin replaces a member's photo; the mutation
 *    invalidates that member; the in-flight response lands and restores the
 *    OLD photo for the next 30 minutes.
 *  - a bulk fetch starts; the admin switches PIN user; `clearMemberPhotoCache`
 *    runs; the in-flight response lands and repopulates the cache with the
 *    PREVIOUS admin's still-valid signed URLs.
 *
 * A caller snapshots this counter before it starts fetching and hands the
 * snapshot back to `writeMemberPhotoCache`, which drops the write if the
 * counter has moved. One global counter rather than per-member ones: both
 * triggers (a photo mutation, an identity change) are rare, and the cost of
 * being over-broad is only that a batch is re-fetched once.
 */
let generation = 0;

/**
 * Snapshot the cache generation before starting a fetch, then pass it to
 * `writeMemberPhotoCache` so a response that raced an invalidation is dropped.
 */
export function getMemberPhotoCacheGeneration(): number {
  return generation;
}

function cacheKey(bucket: string, memberId: string): string {
  return `${bucket}::${memberId}`;
}

/**
 * The soonest any of a photo's signed URLs expires, ignoring variants that were
 * never signed (list surfaces sign only `thumbnail` and `large`, so
 * `original_photo_url_expires_at` is legitimately null there).
 *
 * Returns null when NO variant carries an expiry, which means signing failed
 * outright and every URL on the row is null. Such a row must not be cached as a
 * usable answer or a transient Storage blip would be remembered for 30 minutes.
 */
function earliestSignedUrlExpiry(photo: CacheablePhoto): number | null {
  let earliest: number | null = null;
  const expiries = [
    photo.original_photo_url_expires_at,
    photo.thumbnail_photo_url_expires_at,
    photo.large_photo_url_expires_at,
  ];

  for (const expiry of expiries) {
    if (typeof expiry !== 'number' || Number.isNaN(expiry)) continue;
    if (earliest === null || expiry < earliest) earliest = expiry;
  }

  return earliest;
}

function isRecordUsable(record: CacheRecord<CacheablePhoto>, now: number): boolean {
  if (now - record.cachedAt >= MEMBER_PHOTO_CACHE_TTL_MS) return false;

  // "No photo" carries no URL, so only the data TTL above applies to it.
  if (record.photo === null) return true;

  const expiry = earliestSignedUrlExpiry(record.photo);
  if (expiry === null) return false;

  return expiry > now + SIGNED_URL_EXPIRY_HEADROOM_MS;
}

/**
 * Split `memberIds` into the answers already held and the ids still to fetch.
 *
 * Members cached as having no photo are absent from `photos` AND from
 * `missingIds` — the caller already has the full answer for them, and asking
 * again would reintroduce exactly the traffic this cache removes.
 */
export function readMemberPhotoCache<T extends CacheablePhoto>(
  bucket: string,
  memberIds: readonly string[],
  now: number = Date.now(),
): { photos: Record<string, T>; missingIds: string[] } {
  const photos: Record<string, T> = {};
  const missingIds: string[] = [];

  for (const memberId of memberIds) {
    const record = cache.get(cacheKey(bucket, memberId));

    if (!record || !isRecordUsable(record, now)) {
      missingIds.push(memberId);
      continue;
    }

    if (record.photo !== null) {
      photos[memberId] = record.photo as T;
    }
  }

  return { photos, missingIds };
}

/**
 * Record the outcome of a fetch for `requestedIds`.
 *
 * Every requested id that is absent from `photos` is cached as `null`: the
 * lookup RPCs return a row only for members that HAVE a photo, so an absent id
 * is a definitive "no photo", not a failed read. Caching that is what stops the
 * photo-less half of the roster from being re-queried on every list render.
 *
 * Callers must not invoke this after a failed fetch — a failure is not an
 * answer, and caching it would hide the member's photo for 30 minutes.
 *
 * Pass `options.generation` (snapshotted via `getMemberPhotoCacheGeneration()`
 * before the fetch started) so a response that raced an invalidation or a clear
 * is dropped rather than resurrecting stale data. Returns whether the write was
 * applied.
 */
export function writeMemberPhotoCache<T extends CacheablePhoto>(
  bucket: string,
  requestedIds: readonly string[],
  photos: Record<string, T>,
  options: { generation?: number; now?: number } = {},
): boolean {
  const { generation: startedAt, now = Date.now() } = options;

  // The response raced an invalidation or a clear, so it describes a world that
  // no longer exists. Dropping it costs one re-fetch; applying it would undo
  // the invalidation — see the `generation` comment above.
  if (startedAt !== undefined && startedAt !== generation) {
    return false;
  }

  for (const memberId of requestedIds) {
    cache.set(cacheKey(bucket, memberId), {
      photo: photos[memberId] ?? null,
      cachedAt: now,
    });
  }

  evictIfOversized(now);
  return true;
}

/**
 * Drop one member's entry so the next read re-fetches it. Called by the photo
 * create/delete mutations BEFORE they invalidate React Query, so the refetch
 * those invalidations trigger sees a miss rather than the entry it just
 * replaced.
 */
export function invalidateMemberPhoto(bucket: string, memberId: string): void {
  cache.delete(cacheKey(bucket, memberId));
  generation += 1;
}

/**
 * Wipe the cache. Must be called wherever `queryClient.clear()` is — signing
 * out or switching PIN user leaves signed URLs for member photos in memory
 * otherwise, and the next admin would inherit them.
 */
export function clearMemberPhotoCache(): void {
  cache.clear();
  generation += 1;
}

/** Entry count. Exported for tests and diagnostics only. */
export function getMemberPhotoCacheSize(): number {
  return cache.size;
}

/**
 * Keep the cache bounded: expired entries go first (they are worthless), and
 * only if that is not enough do we evict the oldest still-valid entries.
 */
function evictIfOversized(now: number): void {
  if (cache.size <= MEMBER_PHOTO_CACHE_MAX_ENTRIES) return;

  for (const [key, record] of cache) {
    if (now - record.cachedAt >= MEMBER_PHOTO_CACHE_TTL_MS) cache.delete(key);
  }

  if (cache.size <= MEMBER_PHOTO_CACHE_MAX_ENTRIES) return;

  // Map iterates in insertion order, and every write re-inserts, so the head of
  // the iteration is the least recently written.
  const overflow = cache.size - MEMBER_PHOTO_CACHE_MAX_ENTRIES;
  let removed = 0;
  for (const key of cache.keys()) {
    if (removed >= overflow) break;
    cache.delete(key);
    removed += 1;
  }
}
